/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, HostBinding, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { CommunicationType } from '@gh/builder-shared-data';
import {
  createDialogRoute,
  FormState,
  FormStateBuilder,
  getDialogInitData,
  TextTypes,
  whenCreateOrEditMode,
} from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { TranslationService } from '@gh/core-mls';
import {
  DialogInput,
  DialogService,
  FormMode,
  getControlValue,
  NotificationService,
  responsiveDialogOverlayPaneClass,
  TinymceSettings,
  tmSettingsBuilder,
  WaitingService,
  yesAnswer,
  yesNoConfirmation,
} from '@gh/core-ui';
import { filter$ } from '@gh/rx/operators';

import { isEmpty } from 'lodash';
import { BuilderUserContext } from '../../../user';

import {
  CommunicationsBusinessService,
  EmailDialogConfig,
  isCreateEmailDialogConfig,
} from '../../resources/communications-business.service';
import {
  CommunicationListItem,
  EmailDetails,
  emailDetailsFromCommunicationItem,
  emailDetailsToCommunicationItem,
} from '../../resources/communications.model';

@Component({
  selector: 'gh-email-dialog',
  template: require('./email-dialog.component.html'),
  styles: [require('./email-dialog.component.scss')],
})

export class EmailDialogComponent implements OnInit, DialogInput<EmailDialogConfig> {
  input: EmailDialogConfig;
  form: FormState<EmailDetails>;
  emailTemplates: any[];
  tmResponsiveSettings: TinymceSettings;
  tmSettings: TinymceSettings;
  editor: any;

  constructor(private formStateBuilder: FormStateBuilder,
              private userContext: BuilderUserContext,
              private businessService: CommunicationsBusinessService,
              private waitingService: WaitingService,
              public dialogRef: MatDialogRef<EmailDialogComponent>,
              @Inject(MAT_DIALOG_DATA) private dialogData: any,
              private notificationService: NotificationService,
              private translationService: TranslationService,
              private dialogService: DialogService) {

  }

  @HostBinding('class.m-has-attachments')
  get hasAttachments(): boolean {
    return this.form.createMode && !isEmpty(getControlValue(this.form.group, 'files'));
  }

  get gFolderId(): Maybe<ID> {
    return this.input.gFolderId;
  }

  ngOnInit(): void {
    const { email } = getDialogInitData(this.dialogData);

    this.form = this.formStateBuilder.buildPlain({
      value: {
        ...emailDetailsFromCommunicationItem(email),
        email: this.userContext.sandbox ? void 0 : email.email,
      },
      mode: isCreateEmailDialogConfig(email) &&
      (!this.input.mode || this.input.mode === FormMode.EditMode) ? FormMode.CreateMode : FormMode.ViewMode,
      enabled: whenCreateOrEditMode(),
      children: {
        email: { required: true, type: TextTypes.EmailList },
        emailCc: { type: TextTypes.EmailList },
        emailBcc: { type: TextTypes.EmailList },
        subject: { required: true },
        message: { required: true },
        template: void 0,
        files: void 0,
        contactId: void 0,
        jobId: void 0,
      },
    });
    const events = {
      onChange: (event: any) => {
        this.onMessageChange(event);
      },
      onPaste: (event: any) => {
        this.onMessageChange(event);
      },
      onInit: (editor: any) => {
        this.editor = editor;
      },
    };
    const builder = tmSettingsBuilder()
      .pasteOptions({ asText: false })
      .scayt()
      .styles('body { overflow-wrap: break-word; }')
      // tslint:disable-next-line:no-magic-numbers
      .height(270)
      .statusBar(false)
      .build();
    const builderResponsive = tmSettingsBuilder()
      .pasteOptions({ asText: false })
      .scayt()
      .styles('body { overflow-wrap: break-word; }')
      // tslint:disable-next-line:no-magic-numbers
      .height(270)
      .statusBar(false)
      .build();
    this.tmSettings = {
      ...builder,
      ...events,
    };
    this.tmResponsiveSettings = {
      ...builderResponsive,
      ...events,
    };
    this.loadEmailTemplates();
  }

  onCancel(): void {
    if (this.form.group.dirty || this.form.createMode) {
      this.dialogService
        .confirmation(yesNoConfirmation('msg_changes_not_saved')).pipe(
        filter$(yesAnswer))
        .subscribe(() => this.dialogRef.close());
    } else {
      this.dialogRef.close();
    }
  }

  loadEmailTemplates(): void {
    this.businessService.getEmailTemplates().subscribe((list) => this.emailTemplates = list, this.notificationService.processAll);
  }

  onSelectTemplate({value}: any): void {
    const template = this.emailTemplates.find((item) => item.id === value);
    this.businessService.getFilledEmailTemplate(template.id, this.input.contactId, this.input.jobId).subscribe((res) => {
      this.form.build({
        email: this.form.value.email,
        emailCc: this.form.value.emailCc,
        emailBcc: this.form.value.emailBcc,
        subject: res.subject,
        message: res.body,
        template: this.form.value.template,
        files: this.form.value.files,
        contactId: this.form.value.contactId,
        jobId: this.form.value.jobId,
      });
      if (res.unresolvedPlaceholders.length) {
        const unresolved = res.unresolvedPlaceholders.join(', ');
        this.notificationService.warning(`There are no values for ${unresolved}`);
      }
    });
  }

  onSend(): void {
    if (this.form.group.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.businessService.sendEmail(this.input.entryPoint, this.input, {
      ...emailDetailsToCommunicationItem({
        communicationType: CommunicationType.Email,
        email: this.form.value.email,
        emailCc: this.form.value.emailCc,
        emailBcc: this.form.value.emailBcc,
        subject: this.form.value.subject,
        message: this.form.value.message,
        files: this.form.value.files,
        contactId: this.input.contactId,
        jobId: this.input.jobId,
      }),
      communicationType: CommunicationType.Email,
      contactTypeCode: this.input.email!.contactTypeCode, // tslint:disable-line:no-non-null-assertion
    }).pipe(
      this.waitingService.operator())
      .subscribe(
        (communicationListItem: CommunicationListItem) => {
          this.dialogRef.close(communicationListItem);
        },
        this.notificationService.processAll);
  }
  onMessageChange(event: any): void {
    const text = this.editor.getContent({ format: 'text' }).trim();
    if (!text) {
      this.form.getControl('message').setErrors({required: true});
    }
  }
}

export const emailDialogRoute = createDialogRoute({
  component: EmailDialogComponent,
  init: {
    service: CommunicationsBusinessService,
    selector: 'initEmailDialogView',
  },
  config: {
    disableClose: true,
    maxHeight: '100vh',
    panelClass: responsiveDialogOverlayPaneClass(),
  },
});
